// ─────────────────────────────────────────────────────────────────────────────
// Authenticated Axios instances
//
// Three separate instances — one per service domain:
//   pensionAxios  → pension backend  (getBaseURL)
//   identityAxios → auth/identity    (getIDBaseURL)
//   salaryAxios   → salary backend   (getTransferBaseURL)
//
// All instances share the same request interceptor (inject auth token) and
// response interceptor (handle 401/403). The EmployerId header is set
// directly on pensionAxios by handleSelectEmployer.
// ─────────────────────────────────────────────────────────────────────────────

import axios, { AxiosInstance } from 'axios';
import toast from 'react-hot-toast';
import getBaseURL, { getIDBaseURL, getTransferBaseURL, getAppId } from '../utils/getBaseURL';

const PENSION_APP_ID  = getAppId() || '';
const SALARY_APP_ID   = process.env.REACT_APP_SALARY_APP_ID || '';

// ── Instance creation ─────────────────────────────────────────────────────────

export const pensionAxios: AxiosInstance = axios.create({
  baseURL: getBaseURL() as string,
  headers: { ApplicationId: PENSION_APP_ID },
});

export const identityAxios: AxiosInstance = axios.create({
  baseURL: getIDBaseURL() as string,
  headers: { ApplicationId: PENSION_APP_ID },
});

export const salaryAxios: AxiosInstance = axios.create({
  baseURL: getTransferBaseURL() as string,
  headers: { ApplicationId: SALARY_APP_ID },
});

// ── Shared interceptor factory ────────────────────────────────────────────────

function attachInterceptors(instance: AxiosInstance): void {
  // Request: inject bearer token from localStorage
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('tazat');
    if (token) {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const employerStr = localStorage.getItem('employer');
    if (employerStr) {
      try {
        const employer = JSON.parse(employerStr);
        if (employer.id) {
          config.headers = config.headers ?? {};
          config.headers['EmployerId'] = employer.id;
        }
      } catch (e) {
        // ignore parse error
      }
    }
    return config;
  });

  // Response: handle 401 (unauthenticated) and 403 (forbidden)
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem('tazat');
        localStorage.removeItem('PenUser');
        window.location.href = '/';
      }

      if (error?.response?.status === 403) {
        const method = error.response.config?.method?.toUpperCase();
        const message =
          method === 'GET'
            ? 'You are not authorised to view this resource.'
            : 'You are not authorised to carry out this action.';
        toast.error(message);
      }

      return Promise.reject(error);
    }
  );
}

attachInterceptors(pensionAxios);
attachInterceptors(identityAxios);
attachInterceptors(salaryAxios);

// Also attach to the global axios instance to ensure legacy API calls
// (which haven't been migrated to RTK Query yet) still get the bearer token
// on page refresh.
attachInterceptors(axios);
